import { useMemo, type JSX } from 'react';

import { PriceSimulationInstallationDto } from '@eneco/dc-be/client';
import { Button, CheckTile, CheckboxGroup, Heading, Stack, Stretch, Text } from '@eneco/sparky';
import { EVChargingStation, Heatpump, HomeBattery, SolarPanel } from '@eneco/sparky/illustrations';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRendering } from '@sitecore/types/PriceSimulation';
import { Controller, useFormContext } from 'react-hook-form';

import { INSTALLATION_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

const getInstallationImage = (option: PriceSimulationInstallationDto) => {
  switch (option) {
    case 'SolarPanels':
      return <SolarPanel size="small" />;
    case 'HomeBattery':
      return <HomeBattery size="small" />;
    case 'ChargingStation':
      return <EVChargingStation size="small" />;
    case 'HeatPump':
      return <Heatpump size="small" />;
  }
};

export const InstallationCheckStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRendering>();
  const {
    control,
    trigger,
    formState: { errors },
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const installationOptions = INSTALLATION_OPTIONS.map(option => ({
    name: option,
    label: option,
  }));

  const installationOptionsMap = useMemo(() => {
    const result: { name: PriceSimulationInstallationDto; label: string; image?: JSX.Element }[] = [];
    for (const option of installationOptions) {
      result.push({
        name: option.name,
        label:
          fields.installationTypeStep.installationTypesList.value.options.find(o => o.name === option.name)?.label ??
          option.label,
        image: getInstallationImage(option.name),
      });
    }
    return result;
  }, [installationOptions, fields.installationTypeStep.installationTypesList.value.options]);

  const onNextClick = async () => {
    const isValid = await trigger('installationCheck');
    if (isValid) {
      trackEventBE('pbt_calculation_installations', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.installationTypeStep.title.value}
        </Heading>
        <Text>{fields.installationTypeStep.subTitle.value}</Text>
      </Stack>
      <Stretch>
        <div>
          <Controller
            control={control}
            name={'installationCheck'}
            render={({ field: { onChange, value } }) => {
              const currentValue = Array.isArray(value) ? value : [];

              const handleCheckboxChange = (optionName: (typeof INSTALLATION_OPTIONS)[number]) => {
                if (optionName === 'None') {
                  onChange(['None']);
                } else if (currentValue.includes(optionName)) {
                  onChange(currentValue.filter(val => val !== optionName));
                } else {
                  onChange([...currentValue.filter(val => val !== 'None'), optionName]);
                }
              };

              return (
                <CheckboxGroup
                  label={fields.installationTypeStep.installationTypesList.value.label}
                  error={
                    errors['installationCheck']
                      ? fields.installationTypeStep.installationTypesList.value.requiredMessage
                      : undefined
                  }>
                  {installationOptionsMap.map(({ name, label, image }) => (
                    <CheckTile
                      key={name}
                      name={name}
                      icon={image}
                      isChecked={currentValue.includes(name)}
                      onChange={() => handleCheckboxChange(name)}>
                      {label}
                    </CheckTile>
                  ))}
                </CheckboxGroup>
              );
            }}
          />
        </div>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
